import { Component, type ReactNode, type ErrorInfo } from "react";

interface State {
  error: Error | null;
  componentStack: string | null;
}

export class ErrorBoundary extends Component<{ children: ReactNode }, State> {
  state: State = { error: null, componentStack: null };

  static getDerivedStateFromError(error: Error): Partial<State> {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    this.setState({ error, componentStack: info.componentStack ?? null });
    if (typeof window !== "undefined") {
      (window as unknown as Record<string, unknown>).__rheumhub_last_error__ = {
        message: error.message,
        stack: error.stack,
        componentStack: info.componentStack,
      };
    }
  }

  render() {
    if (!this.state.error) return this.props.children;
    const { error, componentStack } = this.state;
    return (
      <div style={{ padding: 24, fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace", color: "#0f172a", background: "#fff", minHeight: "100vh" }}>
        <h1 style={{ fontSize: 20, fontWeight: 700, marginBottom: 12, color: "#b91c1c" }}>
          RheumHub crashed while rendering
        </h1>
        <p style={{ marginBottom: 16, color: "#475569" }}>
          Screenshot this whole page and send it to support. Then click reload.
        </p>
        <button
          onClick={() => window.location.reload()}
          style={{ marginBottom: 24, padding: "8px 14px", background: "#0f172a", color: "#fff", border: 0, borderRadius: 6, cursor: "pointer" }}
        >
          Reload
        </button>
        <h2 style={{ fontSize: 14, fontWeight: 700, marginTop: 16, marginBottom: 6 }}>Error</h2>
        <pre style={{ whiteSpace: "pre-wrap", wordBreak: "break-word", background: "#fef2f2", border: "1px solid #fecaca", padding: 12, borderRadius: 6, fontSize: 12 }}>
          {error.message}
        </pre>
        {componentStack && (
          <>
            <h2 style={{ fontSize: 14, fontWeight: 700, marginTop: 16, marginBottom: 6 }}>Component stack</h2>
            <pre style={{ whiteSpace: "pre-wrap", wordBreak: "break-word", background: "#f8fafc", border: "1px solid #e2e8f0", padding: 12, borderRadius: 6, fontSize: 12, maxHeight: 320, overflow: "auto" }}>
              {componentStack}
            </pre>
          </>
        )}
        {error.stack && (
          <>
            <h2 style={{ fontSize: 14, fontWeight: 700, marginTop: 16, marginBottom: 6 }}>JS stack</h2>
            <pre style={{ whiteSpace: "pre-wrap", wordBreak: "break-word", background: "#f8fafc", border: "1px solid #e2e8f0", padding: 12, borderRadius: 6, fontSize: 12, maxHeight: 320, overflow: "auto" }}>
              {error.stack}
            </pre>
          </>
        )}
      </div>
    );
  }
}
